/*
 * IMPORTS
 */
import React from 'react' // Npm: react.js library.
import Moment from 'moment' // Npm: moment.js library.
import _ from 'underscore' // Npm: underscore.js library.
import { useQuery } from '@apollo/client' // Npm: Apollo client.
import { IoMdAdd } from 'react-icons/io' // Npm: React icons.
import { HiArrowPath } from 'react-icons/hi2' // Npm: React icons.
import { DataGrid } from '@mui/x-data-grid' // Npm: Material UI Data Grid.
import {
  Button,
  Checkbox,
  Flex,
  Tab,
  TabList,
  Tabs,
  Tooltip,
  useBreakpointValue,
  useDisclosure
} from '@chakra-ui/react' // Npm: Chakra UI components.

/*
 * PACKAGES
 */
import TableSpinner from 'components/TableSpinner'
import Modal from 'components/Modal'
import BlockDestinationAddressUpsert from 'components/BlockDestinationAddressUpsert'
import BlockDestinationAddressDelete from 'components/BlockDestinationAddressDelete'
import BlockDestinationAddressDropDownOptions from 'components/BlockDestinationAddressDropDownOptions'


/*
 * GRAPHS
 */
import BlockDestinationAddressReadQuery from './__query__/index.blockDestinationAddress.read.query'


/*
 * STYLES
 */
import { buttonStyle, tabStyles } from './index.style'


/*
 * OBJECTS
 */
const Index = () => {
  // Const assignment.
  const _Customers = React.useMemo(() => Object.React.App.enums.CONTENT_TRANSLATION_TYPE.enums.map(i => i.key), [])
  const _successFlags = React.useMemo(() => Object.React.App.enums.GRAPHQL_SUCCESSFUL_QUERY_FLAGS.enums.map(i => i.key), [])

  // Hook assignment.
  const [paginationModel, setPaginationModel] = React.useState({
    'pageSize': 8,
    'page': 0
  })
  const [columnVisibilityModel, setColumnVisibilityModel] = React.useState({ 'id': false })
  const [filterModel, setFilterModel] = React.useState({ 'items': [] })
  const [selectAll, setSelectAll] = React.useState(false)
  const [tabIndex, setTabIndex] = React.useState(0)
  const [selectedIds, setSelectedIds] = React.useState([])
  const _isPreviousPageRef = React.useRef(false)
  const _paginationModelRef = React.useRef(paginationModel)
  const _rowCountRef = React.useRef(0)

  const _ResetPagination = React.useCallback(() => {
    _isPreviousPageRef.current = false
    setPaginationModel(j => {
      const nextPaginationModel = { ...j, 'page': 0 }

      _paginationModelRef.current = nextPaginationModel

      return nextPaginationModel
    })
  }, [])
  const _QueryBlockDestinationAddressReadVariables = React.useMemo(() => ({
    'type': _Customers[tabIndex],
    'skip': paginationModel.page * paginationModel.pageSize,
    'take': paginationModel.pageSize
  }), [_Customers, paginationModel.page, paginationModel.pageSize, tabIndex])
  const _QueryBlockDestinationAddressRead = useQuery(
    BlockDestinationAddressReadQuery,
    {
      'variables': _QueryBlockDestinationAddressReadVariables,
      'fetchPolicy': _isPreviousPageRef.current ? 'cache-first' : Object.React.App.fetchPolicy
    }
  )

  const { 'onOpen': onBlockDestinationAddressOpen, 'isOpen': isBlockDestinationAddressOpen, 'onClose': onBlockDestinationAddressClose } = useDisclosure()
  const { 'isOpen': isBlockDestinationAddressUpdateOpen, 'onOpen': onBlockDestinationAddressUpdateOpen, 'onClose': onBlockDestinationAddressUpdateClose } = useDisclosure()
  const { 'isOpen': isBlockDestinationAddressDeleteOpen, 'onOpen': onBlockDestinationAddressDeleteOpen, 'onClose': onBlockDestinationAddressDeleteClose } = useDisclosure()
  const _HandlePaginationModelChange = React.useCallback(__paginationModel => {
    // Only backward pagination with the same page size should prefer cached rows.
    _isPreviousPageRef.current =
      __paginationModel.page < _paginationModelRef.current.page &&
      __paginationModel.pageSize === _paginationModelRef.current.pageSize

    _paginationModelRef.current = __paginationModel
    setPaginationModel(__paginationModel)
  }, [])
  const _HandleFilterModelChange = React.useCallback(__filterModel => {
    _ResetPagination()
    setFilterModel(__filterModel)
  }, [_ResetPagination])
  const _HandleColumnVisibilityModelChange = React.useCallback(newModel => {
    setColumnVisibilityModel(newModel)
  }, [])
  const _HandleTabChange = React.useCallback(r => {
    _ResetPagination()
    setTabIndex(r)
  }, [_ResetPagination])
  const _QueryBlockDestinationAddressReadRowCount = _.pluck(_QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead, '_totalCount')?.[0]
  const rowCount = React.useMemo(() => {
    if (undefined !== _QueryBlockDestinationAddressReadRowCount && null !== _QueryBlockDestinationAddressReadRowCount) _rowCountRef.current = _QueryBlockDestinationAddressReadRowCount

    return _rowCountRef.current
  }, [_QueryBlockDestinationAddressReadRowCount])
  const _tableHeaderHeightRef = React.useRef(0)
  const _isCurrentViewMobile = useBreakpointValue({ 'base': 'false', 'md': false, 'lg': false, 'xl': false, 'sm': true, 'xs': true })

  // Event handler.
  React.useEffect(() => {
    // Reset selectedIds when the data is re-fetched
    if (_QueryBlockDestinationAddressRead.data) {
      // Update states.
      setSelectedIds([])
      setSelectAll(false)
    }
  }, [
    isBlockDestinationAddressUpdateOpen,
    isBlockDestinationAddressOpen,
    isBlockDestinationAddressDeleteOpen,
    tabIndex,
    paginationModel.page
  ])

  // Event handler.
  const _HandleSelectAll = () => {
    // Check if selectAll is true.
    setSelectAll(!selectAll)

    // Check if selectAll is true.
    setSelectedIds(
      selectAll ? [] : _QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead?.map(
        item => item.id
      ) || []
    )
  }

  const _HandleCheckboxChange = id => {
    // Check if id is already in selectedIds.
    if (selectedIds.includes(id)) {
      // Remove id from selectedIds.
      setSelectedIds(selectedIds.filter(selectedId => selectedId !== id))
    } else {
      // Add id to selectedIds.
      setSelectedIds([...selectedIds, id])
    }
  }

  // Event handler.
  const [isRefreshing, setIsRefreshing] = React.useState(false)

  const _HandleRefresh = React.useCallback(async () => {
    setIsRefreshing(true)

    try {
      await _QueryBlockDestinationAddressRead.refetch()
    } finally {
      setIsRefreshing(false)
    }
  }, [_QueryBlockDestinationAddressRead.refetch])

  // Define columns
  const columns = React.useMemo(() => [
    {
      'field': 'checkbox',
      'headerName': '',
      'align': 'center',
      'headerAlign': 'center',
      'width': 60,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true,
      renderHeader: () => (
        <Checkbox bg='gray.100' onChange={_HandleSelectAll} isChecked={selectAll} />
      ),
      renderCell: ({ row }) => (
        <Checkbox
          bg='gray.100'
          isChecked={selectedIds.includes(row.id)}
          onChange={() => _HandleCheckboxChange(row.id)}
        />
      )
    },
    {
      'field': 'sno',
      'headerName': 'S No.',
      'align': 'center',
      'headerAlign': 'center',
      'width': 80,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true
    },
    {
      'field': 'id',
      'headerName': 'ID',
      'align': 'center',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'disableColumnMenu': true,
      'headerAlign': 'center'
    },
    {
      'field': 'customerName',
      'headerName': 'Customer Name',
      'description': 'Name of the customer.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
      'disableColumnMenu': true
    },
    {
      'field': 'destinationAddress',
      'headerName': 'Destination Address',
      'description': 'Blocked destination addresses.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 180,
      'disableColumnMenu': true
    },
    {
      'field': 'senderIds',
      'headerName': "Sender Id's",
      'description': 'Associated sender IDs.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 150,
      'disableColumnMenu': true
    },
    {
      'field': 'createdAt',
      'headerName': 'Created At',
      'description': 'Creation date.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 120
    },
    {
      'field': 'updatedAt',
      'headerName': 'Updated At',
      'description': 'Last update date.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'disableColumnMenu': true,
      'headerAlign': 'center',
      'flex': 1,
      'minWidth': 120
    },
    {
      'field': 'action',
      'headerName': 'Actions',
      'description': 'Available actions.',
      'editable': false,
      'sortable': false,
      'filterable': false,
      'align': 'center',
      'headerAlign': 'center',
      'disableColumnMenu': true,
      'width': 100,
      renderCell: ({ row }) => (
        <BlockDestinationAddressDropDownOptions
          blockDestinationAddressId={row?.id}
          onBlockDestinationAddressUpdate={onBlockDestinationAddressUpdateOpen}
          onBlockDestinationAddressDelete={onBlockDestinationAddressDeleteOpen}
          selectedIds={selectedIds}
          blockDestinationAddressIds={[...selectedIds, row?.id]}
        />
      )
    }
  ], [selectAll, selectedIds])

  // Define rows
  const rows = React.useMemo(() => _.compact(_QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead?.map((item, index) => _successFlags.includes(item.status) ? ({
    ...item,
    'id': item?.id ?? `block-number-${paginationModel.page}-${paginationModel.pageSize}-${index}`,
    'sno': `${(index + (paginationModel.page * paginationModel.pageSize)) + 1}.`,
    'customerName': item?.['TERMINATION' === _Customers[tabIndex] ? 'VendorAccount' : 'CustomerAccount']?.displayName ?? '-',
    'destinationAddress': _.isEmpty(item?.omitDestinationAddress) ? '-' : item?.omitDestinationAddress?.join(', '),
    'senderIds': _.isEmpty(item?.SenderId) ? '-' : _.compact(_.flatten(_.pluck(item?.SenderId, 'senderId')))?.join(', '),
    'createdAt': _.isEmpty(item.createdAt) ? '-' : Moment(item.createdAt).format('YYYY-MM-DD'),
    'updatedAt': _.isEmpty(item?.updatedAt) ? '-' : Moment(item?.updatedAt).format('YYYY-MM-DD')
  }) : null)), [_QueryBlockDestinationAddressRead?.data?.BlockDestinationAddressRead, _Customers, _successFlags, paginationModel.page, paginationModel.pageSize, tabIndex])

  // Return component.
  return (
    <>
      <Flex className="blockDestinationAddress base" height="unset !important">
        <Flex
          display="flex"
          flex={1}
          flexDirection="column"
          minH="100%"
          maxH="100%"
          overflow="hidden"
        >
          <Flex
            display="flex"
            flexDirection="row"
            justifyContent="space-between"
            gap="12px"
            flexWrap="wrap"
            bg="linear-Gradient(73deg,#390EF6 0%, #9244F4 100%)"
            borderTopLeftRadius="20px"
            borderTopRadius="20px"
            p={_isCurrentViewMobile ? "12px" : "22px"}
          >
            <Tooltip label="Block new Number." fontSize="sm">
              <Button
                w="auto"
                onClick={onBlockDestinationAddressOpen}
                leftIcon={<IoMdAdd />}
                style={buttonStyle}
              >
                Block Number
              </Button>
            </Tooltip>
            <Button
              borderRadius="8px"
              fontWeight="450"
              size="sm"
              minW="20px"
              backgroundColor="purple.200"
              color="purple"
              _hover={{ bg: "purple.800", color: "white" }}
              leftIcon={<HiArrowPath />}
              isDisabled={isRefreshing}
              onClick={_HandleRefresh}
            >
              Refresh
            </Button>
          </Flex>
          <Tabs
            variant="enclosed"
            onChange={_HandleTabChange}
            display="flex"
            flexDirection="column"
            align="center"
            w="100%"
            flex={1}
            minH={0}
            bg="white"
            isFitted
          >
            <TabList
              bg="linear-Gradient(73deg,#390EF6 0%, rgba(146, 68, 244, 1) 100%)"
              overflowX={_isCurrentViewMobile ? "auto" : "none"}
            >
              {_Customers.map((u) => (
                <Tab
                  color="white"
                  textDecoration={"line-through"}
                  _focus={{ boxShadow: "none" }}
                  _selected={{ textDecoration: "none" }}
                  style={{ ...tabStyles, border: "none", borderRadius: 0 }}
                  p="1rem"
                  key={u}
                >
                  {u}
                </Tab>
              ))}
            </TabList>
            <Flex
              flex={1}
              minH={0}
              flexDirection="column"
              border="2px solid #3A0FF6"
              borderTop="none"
              borderBottomRadius="15px"
            >
              {/* MUI DataGrid */}
              <DataGrid
                rows={rows}
                columns={columns}
                rowHeight={48}
                rowCount={rowCount}
                paginationMode="server"
                filterMode="client"
                paginationModel={paginationModel}
                onPaginationModelChange={_HandlePaginationModelChange}
                filterModel={filterModel}
                onFilterModelChange={_HandleFilterModelChange}
                pageSizeOptions={[8, 30, 24, 100]}
                slots={{
                  loadingOverlay: () => (
                    <TableSpinner
                      isLoading={true}
                      isEmpty={false}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                  noResultsOverlay: () => (
                    <TableSpinner
                      isLoading={false}
                      isEmpty={true}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                  noRowsOverlay: () => (
                    <TableSpinner
                      isLoading={false}
                      isEmpty={true}
                      chopHeightFromHundredPercentage={
                        _tableHeaderHeightRef?.current?.clientHeight
                      }
                    />
                  ),
                }}
                disableRowSelectionOnClick
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={
                  _HandleColumnVisibilityModelChange
                }
                loading={_QueryBlockDestinationAddressRead?.loading}
                disableColumnMenu={false}
                sx={{
                  flex: 1,
                  minHeight: 0,
                  "& .MuiDataGrid-main": {
                    border: "none",
                    borderRadius: 0,
                  },
                }}
              />
            </Flex>
          </Tabs>
        </Flex>
        <Modal
          size="md"
          title="BD Address Create"
          isOpen={isBlockDestinationAddressOpen}
          onClose={onBlockDestinationAddressClose}
          isCentered={false}
        >
          <BlockDestinationAddressUpsert
            isCreateOnly={true}
            type={_Customers[tabIndex]}
            isVendor={"TERMINATION" === _Customers[tabIndex]}
          />
        </Modal>
        <Modal
          size="md"
          title="BD Address Update"
          isOpen={isBlockDestinationAddressUpdateOpen}
          onClose={onBlockDestinationAddressUpdateClose}
          isCentered={false}
        >
          <BlockDestinationAddressUpsert
            isCreateOnly={false}
            type={_Customers[tabIndex]}
            isVendor={"TERMINATION" === _Customers[tabIndex]}
          />
        </Modal>
        <Modal
          size="md"
          title="BD Address Delete"
          isOpen={isBlockDestinationAddressDeleteOpen}
          onClose={onBlockDestinationAddressDeleteClose}
        >
          <BlockDestinationAddressDelete />
        </Modal>
      </Flex>
    </>
  );
}

/*
 * PROPTYPES
 */
Index.propTypes = {}


/*
 * EXPORT
 */
export default Index
